import { useSaveNotificationPreference } from '@seek/hirer-notification-preference';
import {
  Step,
  type StepActionResult,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import React from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import {
  NOTIFICATION_SELF_CONFIRM_BTN_PRESSED,
  NOTIFICATION_WIDGET_SELF_PREFERENCE_DISPLAYED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { dispatchSetSavedNotificationPreference } from 'src/app/v1/state/dispatcher/notificationsSetting';
import type { AppState } from 'src/types/appState';
import type { TrackingEvent } from 'src/types/trackingData';

import { AddTeamMembersCallout } from '../../../AddTeamMembersCallout';
import { sendNotificationsImpressionEvents } from '../../utils/sendNotificationsSettingEvents';
import { StepNotificationPreferencePanel } from '../StepNotificationPreferencePanel';

import translations from './.vocab';

interface Props {}

export const StepNotificationPreference = ({ ...stepperProps }: Props) => {
  const { teamMembers, userCanManageUsers } = useSelector((state: AppState) => {
    const notificationsSetting = state.session.notificationsSetting;
    return {
      teamMembers: notificationsSetting.savedTeamMemberJobAccessList ?? [],
      userCanManageUsers: state.session.userCanManageUsers,
    };
  }, shallowEqual);
  const { t } = useTranslations(translations);

  const displayAddTeamMemberLink =
    teamMembers.length === 0 && userCanManageUsers;
  const dispatch = useDispatch();
  const saveNotificationPreference = useSaveNotificationPreference();

  const sendSaveEvent = () => {
    const eventData: TrackingEvent = {
      eventName: NOTIFICATION_SELF_CONFIRM_BTN_PRESSED,
    };
    getTrackingAdapter.sendLinkEvent(eventData);
  };
  const handleSaveNotificationPreference =
    async (): Promise<StepActionResult> => {
      const { success, error, notificationPreference } =
        await saveNotificationPreference(sendSaveEvent);

      if (success) {
        dispatch(
          dispatchSetSavedNotificationPreference(notificationPreference),
        );
      }
      return { success, error };
    };

  const onActive = () => {
    sendNotificationsImpressionEvents(
      NOTIFICATION_WIDGET_SELF_PREFERENCE_DISPLAYED,
      displayAddTeamMemberLink,
    );
  };

  const addTeamLinkLabel = t('notification-setting-preference-footer');
  const footerNotification = displayAddTeamMemberLink ? (
    <AddTeamMembersCallout description={addTeamLinkLabel} />
  ) : null;
  return (
    <Step
      onActive={onActive}
      onClickNextAsync={handleSaveNotificationPreference}
      footerNotification={footerNotification}
      {...stepperProps}
    >
      <StepNotificationPreferencePanel />
    </Step>
  );
};
